import React, { useState } from "react";
import {
    Card, CardBody, Row, Col, FormGroup, Label, Input, Button, Collapse
} from "reactstrap";
import { toast } from "react-toastify";
import { useCrSearch, useCrSalesReps } from "./Form/useContractReviewApi";
import CustomerPickerModal from "./CustomerPickerModal";

/**
 * Search panel — collapsible, sits above the tab strip on the landing page.
 * Mirrors the legacy search controls (Pass 1 §1.3): Status, Phase, Quote-disposition,
 * Salesman, SSW Part, Quote/SO #, Customer ID, free-text Search.
 */
export default function ContractReviewSearch({ onResults, defaultOpen = false }) {
    // Go-live parity (2026-09-24): S2 — mounted as the landing page's "Search" tab (open there).
    const [open, setOpen] = useState(!!defaultOpen);
    const [pickerOpen, setPickerOpen] = useState(false);
    const { data: salesReps } = useCrSalesReps();
    const searchMut = useCrSearch();

    // status radio: any | uncompleted | pendingTaskCompletion | pastDue | ready | archived
    const [status, setStatus] = useState("any");
    // phase radio: any | quote | salesOrder | rfq
    const [phase, setPhase] = useState("any");
    // quote sub-radio
    const [quoteSub, setQuoteSub] = useState("any");

    const [salesRepCode, setSalesRepCode] = useState("");
    const [sswPart, setSswPart] = useState("");
    const [quoteOrSo, setQuoteOrSo] = useState("");
    const [custId, setCustId] = useState("");
    const [searchText, setSearchText] = useState("");

    const handleClear = () => {
        setStatus("any"); setPhase("any"); setQuoteSub("any");
        setSalesRepCode(""); setSswPart(""); setQuoteOrSo("");
        setCustId(""); setSearchText("");
    };

    const handleSearch = async () => {
        const filter = {
            statusAny: status === "any",
            statusUncompleted: status === "uncompleted",
            statusPendingTaskCompletion: status === "pendingTaskCompletion",
            statusPastDue: status === "pastDue",
            statusReady: status === "ready",
            statusArchived: status === "archived",

            phaseAny: phase === "any",
            phaseQuote: phase === "quote",
            phaseSalesOrder: phase === "salesOrder",
            phaseRfq: phase === "rfq",

            quoteAny: quoteSub === "any",
            quoteWon: quoteSub === "won",
            quoteLost: quoteSub === "lost",
            quoteAbandoned: quoteSub === "abandoned",

            salesRepCode: salesRepCode || null,
            sswPart: sswPart || null,
            quoteOrSoNumber: quoteOrSo || null,
            custId: custId || null,
            searchText: searchText || null,
        };
        try {
            const rows = await searchMut.mutateAsync(filter);
            onResults(rows);
        } catch {
            toast.error("Search failed — please try again.");
        }
    };

    return (
        <Card className="mb-3">
            <CardBody className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                    <button type="button" className="btn btn-link p-0 fw-semibold" onClick={() => setOpen((o) => !o)}>
                        <i className={`ri-arrow-${open ? "down" : "right"}-s-line align-middle`} />
                        Search Contract Reviews
                    </button>
                    {open && (
                        <div className="d-flex gap-2">
                            <Button size="sm" color="light" onClick={handleClear}>Clear</Button>
                            <Button size="sm" color="primary" onClick={handleSearch} disabled={searchMut.isPending}>
                                {searchMut.isPending ? "Searching…" : "Search"}
                            </Button>
                        </div>
                    )}
                </div>

                <Collapse isOpen={open}>
                    <div className="mt-3">
                        <Row>
                            <Col md={4}>
                                <FormGroup tag="fieldset">
                                    <legend className="fw-semibold fs-6">Status</legend>
                                    <RadioRow value={status} setValue={setStatus} opts={[
                                        ["any", "Any"], ["uncompleted", "Uncompleted (Saved)"],
                                        ["pendingTaskCompletion", "Pending Task Completion"],
                                        ["pastDue", "Past Due"], ["ready", "Ready"], ["archived", "Archived"],
                                    ]} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup tag="fieldset">
                                    <legend className="fw-semibold fs-6">Phase</legend>
                                    <RadioRow value={phase} setValue={setPhase} opts={[
                                        ["any", "Any"], ["quote", "Quote"],
                                        ["salesOrder", "Sales Order"], ["rfq", "RFQ"],
                                    ]} />
                                </FormGroup>
                            </Col>
                            {phase === "quote" && (
                                <Col md={4}>
                                    <FormGroup tag="fieldset">
                                        <legend className="fw-semibold fs-6">Quote Disposition</legend>
                                        <RadioRow value={quoteSub} setValue={setQuoteSub} opts={[
                                            ["any", "Any"], ["won", "Won"], ["lost", "Lost"], ["abandoned", "Abandoned (>45d)"],
                                        ]} />
                                    </FormGroup>
                                </Col>
                            )}
                        </Row>

                        <Row className="mt-2">
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="fw-semibold">Salesperson</Label>
                                    <Input type="select" value={salesRepCode} onChange={(e) => setSalesRepCode(e.target.value)}>
                                        <option value="">(all)</option>
                                        {(salesReps || []).map((sr) => (
                                            <option key={sr.salesRepCode} value={sr.salesRepCode}>{sr.name}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="fw-semibold">SSW Part</Label>
                                    <Input type="text" value={sswPart} onChange={(e) => setSswPart(e.target.value)} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="fw-semibold">Quote # / SO #</Label>
                                    <Input type="text" value={quoteOrSo} onChange={(e) => setQuoteOrSo(e.target.value)} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="fw-semibold">Customer ID</Label>
                                    <div className="d-flex gap-1">
                                        <Input type="text" value={custId} onChange={(e) => setCustId(e.target.value)} />
                                        <Button color="light" size="sm" onClick={() => setPickerOpen(true)}>
                                            <i className="ri-search-line" />
                                        </Button>
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="fw-semibold">Free-text Search</Label>
                                    <Input
                                        type="text"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        placeholder="Searches Title, Customer, post-quote description, notes and responses"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                </Collapse>
            </CardBody>

            <CustomerPickerModal
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onPick={({ custId }) => { setCustId(custId); setPickerOpen(false); }}
            />
        </Card>
    );
}

function RadioRow({ value, setValue, opts }) {
    return (
        <div className="d-flex flex-wrap gap-3">
            {opts.map(([v, label]) => (
                <label key={v} className="form-check d-flex align-items-center gap-1 mb-0">
                    <input
                        type="radio"
                        className="form-check-input"
                        checked={value === v}
                        onChange={() => setValue(v)}
                    />
                    <span>{label}</span>
                </label>
            ))}
        </div>
    );
}
